import { DataTable } from "@/components/common/DataTable";
import MultiSelect, { MultiSelectOption } from "@/components/common/MultiSelect";
import { Input } from "@/components/ui/input";
import { useApiClient } from "@/context/ApiContext";
import { useStaffingTable } from "@/hooks/useStaffingTable";
import { getDistinctApplicantCities, getStaffingApplicantsWithFilters } from "@/services/applicant/Applicant";
import { StaffingApplicant } from "@/types/common/applicant";
import { PAGE_SIZE } from "@/utils/constants";
import {
    APPLICANT_ROLE_OPTIONS,
    APPLICANT_STATUS_OPTIONS,
    ApplicantFilters,
    buildApplicantCityLookupFilter,
    buildApplicantFilter,
    DEFAULT_APPLICANT_STATUSES,
} from "@/utils/staffingUtil";
import { useEffect, useMemo, useState } from "react";
import { applicantColumns } from "../columns/StaffingDashboardColumns";

interface ApplicantsTabProps { region: string | null; }

const DEFAULT_FILTERS: ApplicantFilters = {
    name: '',
    statuses: DEFAULT_APPLICANT_STATUSES,
    roles: [],
    cities: [],
};

const ApplicantsTab = ({ region }: ApplicantsTabProps) => {
    const apiClient = useApiClient();
    const [cityOptions, setCityOptions] = useState<MultiSelectOption[]>([]);

    const { data, loading, error, total, filters, applyFilter, handleNameChange, handleParamsChange } =
        useStaffingTable<StaffingApplicant, ApplicantFilters>({
            region,
            defaultFilters: DEFAULT_FILTERS,
            buildFilter: buildApplicantFilter,
            defaultOrderBy: 'FullName asc',
            fetcher: getStaffingApplicantsWithFilters,
        });

    const { statuses, roles } = filters;
    const cityLookupFilters = useMemo(() => ({ name: '', statuses, roles, cities: [] }), [statuses, roles]);
    useEffect(() => {
        if (!region) return;
        getDistinctApplicantCities(buildApplicantCityLookupFilter(region, cityLookupFilters), apiClient)
            .then(res => setCityOptions((res.data as { City: string }[]).map(v => ({ label: v.City, value: v.City }))))
            .catch(() => {});
    }, [region, cityLookupFilters, apiClient]);

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
                <Input
                    placeholder="Search by name..."
                    value={filters.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className="w-48"
                />
                <MultiSelect
                    options={APPLICANT_STATUS_OPTIONS}
                    value={filters.statuses}
                    onChange={(statuses) => applyFilter({ ...filters, statuses })}
                    placeholder="Status"
                    searchPlaceholder="Search statuses..."
                />
                <MultiSelect
                    options={APPLICANT_ROLE_OPTIONS}
                    value={filters.roles}
                    onChange={(roles) => applyFilter({ ...filters, roles })}
                    placeholder="Role"
                    searchPlaceholder="Search roles..."
                />
                <MultiSelect
                    options={cityOptions}
                    value={filters.cities}
                    onChange={(cities) => applyFilter({ ...filters, cities })}
                    placeholder="City"
                    searchPlaceholder="Search cities..."
                />
            </div>
            <DataTable
                data={data}
                columns={applicantColumns}
                loading={loading}
                error={error ?? undefined}
                serverSide={{ total, pageSize: PAGE_SIZE, onParamsChange: handleParamsChange }}
            />
        </div>
    );
};

export default ApplicantsTab;
